import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Users, FileText, Receipt, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, useMe, errMsg } from "@/lib/api";
import { AdminInvoices } from "@/components/AdminInvoices";
import { GstReport } from "@/components/GstReport";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-xl border border-paper-border bg-white p-6">
    <div className="flex items-center justify-between"><p className="eyebrow">{label}</p><Icon className="h-4 w-4 text-thread" /></div>
    <p className="mt-4 font-serif text-4xl text-ink" data-testid={testId}>{value}</p>
  </div>
);

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function Admin() {
  const api = useApi();
  const me = useMe();
  const [stats, setStats] = useState(null);
  const [people, setPeople] = useState(null);

  const loadUsers = () => api.get("/users").then((r) => setPeople(r.data));
  useEffect(() => {
    if (me?.role !== "admin") return;
    api.get("/admin/stats").then((r) => setStats(r.data));
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, api]);

  const changeRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}/role`, { role }); toast.success("Role updated"); loadUsers(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (me && me.role !== "admin") return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-paper">
      <AppHeader />
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-14" data-testid="admin-page">
        <p className="eyebrow">Owner view</p>
        <h1 className="mt-2 text-4xl text-ink sm:text-5xl">Business at a glance</h1>
        {!stats ? <Loader2 className="mt-10 h-6 w-6 animate-spin text-thread" /> : (
          <>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat icon={Users} label="Users" value={stats.users} testId="admin-users-stat" />
              <Stat icon={FileText} label="Documents" value={stats.documents} testId="admin-documents-stat" />
              <Stat icon={Receipt} label="Purchases" value={stats.purchases} testId="admin-purchases-stat" />
              <Stat icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(2)} AUD`} testId="admin-revenue-stat" />
            </div>
            <h2 className="mt-14 text-2xl text-ink">Recent purchases</h2>
            <div className="mt-4 overflow-x-auto rounded-xl border border-paper-border bg-white">
              <Table data-testid="admin-purchases-table">
                <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Product</TableHead><TableHead>Amount</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                <TableBody>
                  {stats.recent_purchases.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-ink-soft">No purchases yet.</TableCell></TableRow>}
                  {stats.recent_purchases.map((p, i) => (
                    <TableRow key={i}><TableCell>{p.email || "—"}</TableCell><TableCell>{p.product}</TableCell><TableCell>${p.amount.toFixed(2)} {(p.currency || "aud").toUpperCase()}</TableCell><TableCell className="text-ink-soft">{fmt(p.created_at)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        {stats && <GstReport api={api} />}
        {stats && <AdminInvoices api={api} />}
        {people && (
          <>
            <h2 className="mt-14 text-2xl text-ink">Users</h2>
            <div className="mt-4 overflow-x-auto rounded-xl border border-paper-border bg-white">
              <Table data-testid="admin-users-table">
                <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Joined</TableHead><TableHead>Pass until</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
                <TableBody>
                  {people.users.map((u) => (
                    <TableRow key={u.user_id}>
                      <TableCell>{u.email || u.user_id}</TableCell>
                      <TableCell className="text-ink-soft">{fmt(u.created_at)}</TableCell>
                      <TableCell className="text-ink-soft">{fmt(u.pass_expires_at)}</TableCell>
                      <TableCell>
                        <Select value={u.role} onValueChange={(r) => changeRole(u.user_id, r)}>
                          <SelectTrigger className="h-8 w-28" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{people.roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
